import { useState, useRef, useEffect } from 'react'
import { Circle, Plus, X } from 'lucide-react'
import Input from './input/InputField'

interface Option {
    id: number | string
    name: string
    color?: string
    image?: string
}

interface SelectProps {
    options: Option[]
    icon?: React.ReactNode
    multiple?: boolean
    allowNew?: boolean
    onChange: (selected: Option[]) => void
    handleNew?: (name: string) => void
    value: Option[] // Adicionado para refletir o estado externo
    error?: boolean
}

const Select = ({
    options,
    icon,
    multiple = false,
    allowNew = false,
    onChange,
    handleNew,
    value,
    error
}: SelectProps) => {
    const [data, setData] = useState<Option[]>(options)
    const [selectedOptions, setSelectedOptions] = useState<Option[]>([])
    const [showDropdown, setShowDropdown] = useState(false)
    const [search, setSearch] = useState('')
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const dropdownRef = useRef<HTMLDivElement | null>(null)
    const selectRef = useRef<HTMLDivElement | null>(null)

    const addOption = (option: Option) => {
        const newSelected = multiple ? [...value, option] : [option]
        onChange(newSelected)
        setSearch('')
        setShowDropdown(false)
    }

    const removeOption = (id: number | string) => {
        const newSelected = value.filter(option => option.id !== id)
        onChange(newSelected)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && filteredOptions.length > 0) {
            event.preventDefault()
            event.stopPropagation()
            addOption(filteredOptions[0])
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                selectRef.current &&
                !selectRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setShowDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const filteredOptions = data
        .filter(option => option.name.toLowerCase().includes(search.toLowerCase()))
        .filter(option => !value.some(o => o.id === option.id))

    useEffect(() => {
        setData(options)
    }, [options])

    return (
        <div className='relative w-full text-sm' ref={selectRef}>
            <div
                className={`flex flex-wrap gap-1 items-center justify-between w-full border dark:border-gray-700 px-4 py-[0.35rem] min-h-[2.8rem] rounded-lg text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 cursor-pointer overflow-x-hidden ${
                    showDropdown && 'border-2 border-brand-500 dark:border-brand-500'
                }`}
                onClick={() => setShowDropdown(prev => !prev)}
            >
                <div className='flex items-center flex-wrap gap-1'>
                    {value.length > 0 ? (
                        value.map(option => (
                            <span
                                key={option.id}
                                className='flex items-center gap-1 text-xs px-3 py-2 bg-gray-100/90 dark:bg-gray-700/80 rounded-lg text-gray-800 dark:text-gray-200 border'
                                style={{
                                    border: option.color ? `1px solid ${option.color}` : 'none'
                                }}
                            >
                                <p className='font-medium'>{option.name}</p>
                                <X
                                    size={14}
                                    className='cursor-pointer hover:text-error-400'
                                    onClick={e => {
                                        removeOption(option.id)
                                        e.stopPropagation()
                                    }}
                                />
                            </span>
                        ))
                    ) : (
                        <p className='px-2 text-gray-400 dark:text-gray-500'>- Selecione -</p>
                    )}
                </div>
                {icon && (
                    <button ref={buttonRef} type='button' className='px-2 py-1 text-gray-500 dark:text-gray-400'>
                        {icon}
                    </button>
                )}
            </div>
            {showDropdown && (
                <div
                    ref={dropdownRef}
                    className='absolute mt-1 w-full bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl shadow-md z-50 py-2'
                >
                    <div className='px-4 pt-2'>
                        <Input
                            placeholder='Buscar...'
                            value={search}
                            size='sm'
                            onKeyDown={e => {
                                handleKeyDown(e)
                                e.stopPropagation()
                            }}
                            onChange={e => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className='max-h-[35vh] overflow-y-auto'>
                        {filteredOptions.map(option => (
                            <div
                                key={option.id}
                                className='flex items-center py-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-5'
                                onClick={() => addOption(option)}
                            >
                                {/* Circulo com cor */}
                                {option.color && (
                                    <Circle
                                        className='mr-2 flex-shrink-0 w-2 h-2'
                                        size={12}
                                        fill={option.color}
                                        color={option.color}
                                    />
                                )}
                                {/* Foto */}
                                {option.image && option.image !== '' && (
                                    <img className='w-7 h-7 rounded-full mr-3' src={option.image} alt='user photo' />
                                )}
                                <span className='truncate font-normal text-gray-800 dark:text-gray-200'>
                                    {option.name}
                                </span>
                            </div>
                        ))}
                    </div>
                    {allowNew && search && handleNew && (
                        <div
                            className='flex items-center px-4 py-2 cursor-pointer hover:text-brand-600 text-brand-500 dark:text-brand-400 dark:hover:text-brand-300'
                            onClick={() => {
                                handleNew(search)
                                setShowDropdown(false)
                            }}
                        >
                            <Plus size={16} className='mr-1' /> {`Cadastrar "${search}"`}
                        </div>
                    )}
                </div>
            )}
            {error && !value.length && <p className='text-error-500 text-xs mt-1'>Este campo é obrigatório</p>}
        </div>
    )
}

export default Select
